import React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import Popup from '../components/Popup';
import Form from './Form';
import Select from '../components/Fields/Select';
import Input, { checkboxStyles } from '../components/Fields/Input';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { connect } from 'react-redux';
import {
  getBracketById,
  updateNode,
  updateNodeParticipant,
  getNodeByIdFromBracket,
  hasVisibleConfirm,
} from '../redux/app';
import Text, { getTranslation } from '../components/Text';
import { navigateBack, fetchApi } from '../utils';
import { selectToken } from '../redux/user.selectors';
import orderBy from 'lodash/orderBy';

const s = StyleSheet.create({
  fieldset: {
    border: '1px solid #eee',
    marginBottom: 20,
    padding: '8px 12px 0',
    width: '100%',
  },
  fieldset__playerBaseBlock: {
    display: 'flex',
  },
  fieldset_column: {
    flexDirection: 'column',
  },
  fieldset__input_score: {
    flexBasis: '35%',
    marginLeft: 12,
    maxWidth: '35%',
  },
  matchDateFieldWrapper: {
    display: 'flex',
  },
  matchDateField: {
    flex: '1 0 60%',
    maxWidth: '60%',
  },
  matchTimeField: {
    flex: '1 1 40%',
    marginLeft: 12,
    maxWidth: '40%',
  },
});

class BracketNodeEditor extends Form {
  constructor(props) {
    super(props);

    this.state = {
      values: this.mapProps(),
      didChange: false,
      loading: true,
    };
  }

  async componentDidMount() {
    const {
      data: { node },
    } = await fetchApi({
      url: `api/v2/node/${this.props.match.params.nodeId}`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.props.token}`,
      },
    });

    this.setState({ loading: false, values: { ...node, ...this.mapProps() } });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      JSON.stringify(prevProps.defaultValues) !==
      JSON.stringify(this.props.defaultValues)
    ) {
      this.setState({
        values: this.mapProps(),
      });
    }

    if (
      prevState &&
      prevState.values &&
      (prevState.values.player1 !== this.state.values.player1 ||
        prevState.values.player2 !== this.state.values.player2) &&
      this.state.values.winner !== this.state.values.player1 &&
      this.state.values.winner !== this.state.values.player2
    ) {
      this.setState({
        values: {
          ...this.state.values,
          winner: '',
        },
      });
    }
  }

  mapProps = () => {
    return {
      ...(this.props.defaultValues.participants || []).reduce(
        (result, participant, index) => ({
          ...result,
          [`player${index + 1}`]: participant.participant_id || '',
          [`player${index + 1}Score`]: participant.score || '',
          [`player${index + 1}DisplayNotes`]: participant.displayNotes || '',
          [`player${index + 1}Notes`]: participant.notes || '',
        }),
        {}
      ),
      winner: this.props.defaultValues.winner || '',
      next: this.props.defaultValues.next,
      matchDate: this.props.defaultValues.matchDate,
      matchTime: this.props.defaultValues.matchTime,
      displayNotes: this.props.defaultValues.displayNotes || false,
      displayMatchDate: this.props.defaultValues.displayMatchDate || false,
      notes: this.props.defaultValues.notes || '',
      cta: this.props.defaultValues.cta || '',
    };
  };

  nameSpace = 'app.tree.node.edit';

  getAvailableParticipants = () => {
    const blacklisted = this.props.nodes
      .reduce(
        (result, node) => {
          if (
            !node.initial ||
            node.id === Number(this.props.match.params.nodeId)
          ) {
            return result;
          }

          return [...result, ...node.participants.map(({ id }) => id)];
        },
        [this.state.values.player1, this.state.values.player2]
      )
      .filter(Boolean);

    return orderBy(
      this.props.participants.map((participant) => ({
        value: participant.id,
        label: participant.name,
        disabled: blacklisted.includes(participant.id),
      })),
      'disabled'
    );
  };

  getParticipantNameById = (id) =>
    this.props.participants.find((participant) => participant.id === id).name;

  renderVideoFields = () => {
    const fields = [
      {
        name: 'video',
        component: Input,
        disabled: !!this.state.values.cta,
      },
      {
        name: 'cta',
        component: Input,
        disabled: !!this.state.values.video,
      },
    ];

    return this.createFields(fields);
  };

  renderWinnerField = () => {
    const { player1, player2 } = this.state.values;
    const fields = [
      {
        name: 'winner',
        component: Select,
        disabled: !this.state.values.player1 || !this.state.values.player2,
        options: [
          { value: '', label: getTranslation('app.tree.node.edit.no.winner') },
          !!player1 &&
            !!player2 && {
              value: player1,
              label: this.getParticipantNameById(player1),
            },
          !!player1 &&
            !!player2 && {
              value: player2,
              label: this.getParticipantNameById(player2),
            },
        ].filter(Boolean),
      },
    ];

    return this.createFields(fields);
  };

  renderPlayerFields = () => {
    const participants = this.getAvailableParticipants();
    const players = [...Array(2).keys()];

    const playerFields = players.map((_, index) => {
      const playerBaseName = `player${index + 1}`;

      return [
        {
          name: playerBaseName,
          component: Select,
          options: participants,
          isSearchable: true,
          isClearable: true,
          disabled:
            !this.props.defaultValues.initial || !!this.state.values.winner,
        },
        {
          name: `${playerBaseName}Score`,
          component: Input,
          maxLength: 3,
          __containerClass: s.fieldset__input_score,
        },
      ];
    });

    return playerFields.map((playerField, index) => {
      const playerBaseName = `player${index + 1}`;
      return (
        <fieldset key={index} className={css(s.fieldset)}>
          <legend>
            <Text
              messageId={this.getTranslationKey(`${playerBaseName}Fieldset`)}
            />
          </legend>
          <div className={css(s.fieldset__playerBaseBlock)}>
            {this.createFields(playerField)}
          </div>
          <div>
            {this.createFields([
              {
                name: `${playerBaseName}DisplayNotes`,
                component: Input,
                type: 'checkbox',
                __containerClass: checkboxStyles.checkboxContainer,
                __inputClass: checkboxStyles.checkboxInput,
              },
              {
                name: `${playerBaseName}Notes`,
                component: Input,
                disabled: !this.state.values[`${playerBaseName}DisplayNotes`],
              },
            ])}
          </div>
        </fieldset>
      );
    });
  };

  renderMatchDateAndTimeFields = () => {
    const fields = [
      {
        name: 'matchDate',
        component: Input,
        type: 'date',
        max: '3000-01-01',
        disabled: !this.state.values.displayMatchDate,
        __containerClass: s.matchDateField,
        error: this.state.invalidDate && `${this.nameSpace}.error.invalidDate`,
      },
      {
        name: 'matchTime',
        component: Input,
        type: 'time',
        disabled: !this.state.values.displayMatchDate,
        __containerClass: s.matchTimeField,
      },
    ];

    return (
      <fieldset className={css(s.fieldset, s.fieldset_column)}>
        {this.createFields([
          {
            name: 'displayMatchDate',
            component: Input,
            type: 'checkbox',
            __containerClass: checkboxStyles.checkboxContainer,
            __inputClass: checkboxStyles.checkboxInput,
          },
        ])}
        <div className={css(s.matchDateFieldWrapper)}>
          {this.createFields(fields)}
        </div>
      </fieldset>
    );
  };

  renderNoteFields = () => {
    const fields = [
      {
        name: 'displayNotes',
        component: Input,
        type: 'checkbox',
        __containerClass: checkboxStyles.checkboxContainer,
        __inputClass: checkboxStyles.checkboxInput,
      },
      {
        name: 'notes',
        component: Input,
        disabled: !this.state.values.displayNotes,
      },
    ];

    return (
      <fieldset className={css(s.fieldset, s.fieldset_column)}>
        {this.createFields(fields)}
      </fieldset>
    );
  };

  getTranslationKey = (name) => {
    return `${this.nameSpace}.${name}`;
  };

  render() {
    if (this.props.hasVisibleConfirm) {
      return null;
    }

    return (
      <Popup>
        {this.state.loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {this.renderPlayerFields()}
            {this.renderWinnerField()}
            {this.renderMatchDateAndTimeFields()}
            {this.renderVideoFields()}
            <this.Buttons cancelMessageId="app.close" />
          </>
        )}
      </Popup>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const bracket = getBracketById(state, ownProps.match.params.id);
  return {
    ...bracket,
    hasVisibleConfirm: hasVisibleConfirm(state),
    defaultValues: getNodeByIdFromBracket(
      bracket.nodes,
      ownProps.match.params.nodeId
    ),
    token: selectToken(state),
  };
};

const mapDispatchToProps = Form.mapDispatchToProps((dispatch, ownProps) => ({
  save: async (data = {}) => {
    const {
      player1,
      player1Score,
      player1DisplayNotes,
      player1Notes,
      player2,
      player2Score,
      player2DisplayNotes,
      player2Notes,
      winner,
      video,
      cta,
      displayMatchDate,
      matchDate,
      matchTime,
      notes,
    } = data;

    await dispatch(
      updateNode(
        {
          participantId: [player1, player2].filter(Boolean),
          winnerId: winner,
          video,
          cta,
          displayMatchDate,
          matchDate: matchDate === '' ? null : matchDate,
          matchTime: matchTime === '' ? null : matchTime,
          notes,
        },
        { treeId: ownProps.match.params.id, id: ownProps.match.params.nodeId }
      )
    );

    await dispatch(
      updateNodeParticipant(
        {
          score: player1Score,
          displayNotes: player1DisplayNotes,
          notes: player1Notes,
        },
        {
          treeId: ownProps.match.params.id,
          nodeId: ownProps.match.params.nodeId,
          participantId: player1,
        }
      )
    );

    await dispatch(
      updateNodeParticipant(
        {
          score: player2Score,
          displayNotes: player2DisplayNotes,
          notes: player2Notes,
        },
        {
          treeId: ownProps.match.params.id,
          nodeId: ownProps.match.params.nodeId,
          participantId: player2,
        }
      )
    );

    navigateBack();
  },
}));

export default connect(mapStateToProps, mapDispatchToProps)(BracketNodeEditor);
